import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getDisputeNotes } from '@/lib/demo-data/dispute-notes';
import { updateDisputeNoteDemoResponse } from '@/services/demo/dispute-notes-demo.service';
import type {
  CreateDisputeNoteRequest,
  DeleteDisputeNoteRequest,
  DisputeNote,
  DisputeNoteApiItem,
  DisputeNotesApiResponse,
  UpdateDisputeNoteRequest,
} from '@/types/dispute-note';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';
import { CUSTOMER_TIMELINE_QUERY_KEYS, invalidateCustomerTimeline } from '@/lib/note-query-keys';
import { useBulkDeleteNotes } from './useBulkDeleteNotes';

function mapApiItemToDisputeNote(item: DisputeNoteApiItem): DisputeNote {
  return {
    id: String(item.id),
    disputeId: String(item.disputeId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: item.createdByUserId === null ? '' : String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch dispute notes
 * Handles both Demo and Live modes
 */
export function useDisputeNotes(disputeId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = disputeId ? getDisputeNotes(disputeId) : [];

  return useModeAwareQuery<DisputeNote[], Error>(
    ['dispute-notes', disputeId],
    async ({ signal }) => {
      if (!disputeId) return [];
      const response = await apiGet<DisputeNotesApiResponse>(`/api/v1/disputes/${disputeId}/notes`, undefined, signal);
      return (response.notes || []).map(mapApiItemToDisputeNote);
    },
    mockNotes,
    {
      enabled: !!disputeId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to add a new dispute note
 * Handles both Demo and Live modes
 */
export function useAddDisputeNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateDisputeNoteRequest & { userId: string; userName: string }) => {
      const response = await apiPost<DisputeNoteApiItem>(`/api/v1/disputes/${request.disputeId}/notes`, {
        content: request.content,
      });
      return mapApiItemToDisputeNote(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dispute-notes', variables.disputeId] });
      queryClient.invalidateQueries({ queryKey: ['dispute-recent-activities', variables.disputeId] });
      invalidateCustomerTimeline(queryClient);
    },
  });
}

/**
 * Hook to edit an existing dispute note
 * Handles both Demo and Live modes
 */
export function useUpdateDisputeNote() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<DisputeNote, Error, UpdateDisputeNoteRequest>(
    async (request) => {
      const response = await apiPut<DisputeNoteApiItem>(
        `/api/v1/disputes/${request.disputeId}/notes/${request.noteId}`,
        { content: request.content },
      );
      return mapApiItemToDisputeNote(response);
    },
    updateDisputeNoteDemoResponse,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['dispute-notes', variables.disputeId] });
        queryClient.invalidateQueries({ queryKey: ['dispute-recent-activities', variables.disputeId] });
        queryClient.invalidateQueries({ queryKey: ['dispute-audit-logs'] });
        invalidateCustomerTimeline(queryClient);
      },
    },
  );
}

/**
 * Hook to delete a dispute note
 * Handles both Demo and Live modes
 */
export function useDeleteDisputeNote() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, DeleteDisputeNoteRequest>(
    async (request) => {
      await apiDelete<void>(`/api/v1/disputes/${request.disputeId}/notes/${request.noteId}`);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({ queryKey: ['dispute-notes', variables.disputeId] });
        queryClient.invalidateQueries({ queryKey: ['dispute-recent-activities', variables.disputeId] });
        queryClient.invalidateQueries({ queryKey: ['dispute-audit-logs'] });
        invalidateCustomerTimeline(queryClient);
      },
    },
  );
}

/**
 * Hook to delete several dispute notes in one call
 * Handles both Demo and Live modes
 */
export function useDeleteBulkDisputeNotes() {
  return useBulkDeleteNotes('/api/v1/disputes/notes/bulk-delete', [
    'dispute-notes',
    'dispute-recent-activities',
    'dispute-audit-logs',
    ...CUSTOMER_TIMELINE_QUERY_KEYS,
  ]);
}
